import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ComponentType,
  DetailedHTMLProps,
  ReactNode
} from 'react';
import { isValidElement, useEffect, useRef } from 'react';
import type { ButtonProps as AriaButtonProps } from 'react-aria-components';
import { Button as AriaButton, Link as AriaLink } from 'react-aria-components';
import { cx, sortCx } from '../../../utils/cx';
import { isReactComponent } from '../../../utils/is-react-component';

export const styles = sortCx({
  common: {
    root: [
      'group relative inline-flex h-max cursor-pointer items-center justify-center whitespace-nowrap outline-brand transition duration-100 ease-linear before:absolute',
      // When button is used within `InputGroup`
      'in-data-input-wrapper:shadow-xs in-data-input-wrapper:focus:!z-50 in-data-input-wrapper:in-data-leading:-mr-px in-data-input-wrapper:in-data-leading:rounded-r-none in-data-input-wrapper:in-data-leading:before:rounded-r-none in-data-input-wrapper:in-data-trailing:-ml-px in-data-input-wrapper:in-data-trailing:rounded-l-none in-data-input-wrapper:in-data-trailing:before:rounded-l-none',
      // Disabled styles
      'disabled:cursor-not-allowed disabled:text-fg-disabled',
      // Icon styles
      'disabled:*:data-icon:text-fg-disabled_subtle',
      // Same as `icon` but for SSR icons that cannot be passed to the client as functions.
      '*:data-icon:pointer-events-none *:data-icon:size-5 *:data-icon:shrink-0 *:data-icon:transition-inherit-all'
    ].join(' '),
    icon: 'pointer-events-none size-5 shrink-0 transition-inherit-all'
  },
  sizes: {
    xs: {
      root: 'gap-1 rounded-[var(--gn-button-border-radius)] px-[var(--gn-button-padding-x-small)] py-[var(--gn-button-padding-y-small)] text-[length:var(--gn-typography-text-xs-size,0.75rem)] leading-[var(--gn-typography-text-xs-line-height,1.125rem)] font-normal before:rounded-[calc(var(--gn-button-border-radius)-1px)] data-icon-only:p-1.5',
      linkRoot: 'gap-0.5'
    },
    sm: {
      root: [
        'gap-1 rounded-[var(--gn-button-border-radius)] px-[var(--gn-button-padding-x-small)] py-[var(--gn-button-padding-y-small)] text-[length:var(--gn-button-font-size-small)] leading-[var(--gn-button-line-height-small)] font-semibold before:rounded-[calc(var(--gn-button-border-radius)-1px)] data-icon-only:p-2',
        'in-data-input-wrapper:px-3.5 in-data-input-wrapper:py-2.5 in-data-input-wrapper:data-icon-only:p-2.5'
      ].join(' '),
      linkRoot: 'gap-1'
    },
    md: {
      root: [
        'gap-1 rounded-[var(--gn-button-border-radius)] px-[var(--gn-button-padding-x-medium)] py-[var(--gn-button-padding-y-medium)] text-[length:var(--gn-button-font-size)] leading-[var(--gn-button-line-height)] font-semibold before:rounded-[calc(var(--gn-button-border-radius)-1px)] data-icon-only:p-2.5',
        'in-data-input-wrapper:gap-1.5 in-data-input-wrapper:px-4 in-data-input-wrapper:text-md in-data-input-wrapper:data-icon-only:p-3'
      ].join(' '),
      linkRoot: 'gap-1'
    },
    lg: {
      root: 'gap-1.5 rounded-[var(--gn-button-border-radius)] px-[var(--gn-button-padding-x-large)] py-[var(--gn-button-padding-y-large)] text-[length:var(--gn-button-font-size-xlarge)] leading-[var(--gn-button-line-height-xlarge)] font-semibold before:rounded-[calc(var(--gn-button-border-radius)-1px)] data-icon-only:p-3',
      linkRoot: 'gap-1.5'
    },
    xl: {
      root: 'gap-1.5 rounded-[var(--gn-button-border-radius)] px-[var(--gn-button-padding-x-xlarge)] py-[var(--gn-button-padding-y-xlarge)] text-[length:var(--gn-button-font-size-xlarge)] leading-[var(--gn-button-line-height-xlarge)] font-semibold before:rounded-[calc(var(--gn-button-border-radius)-1px)] data-icon-only:p-3.5',
      linkRoot: 'gap-1.5'
    }
  },

  colors: {
    brand: {
      root: [
        'bg-[var(--gn-color-brand-primary-500)] text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset',
        'before:absolute before:inset-px before:border before:border-white/20 before:rounded-[calc(var(--gn-button-border-radius)-1px)]',
        'hover:bg-[var(--gn-color-brand-primary-600)] data-loading:bg-[var(--gn-color-brand-primary-600)]',
        'disabled:bg-[var(--gn-color-brand-primary-200)] disabled:text-white/70 disabled:shadow-xs disabled:ring-disabled_subtle',
        '*:data-icon:text-[var(--gn-color-brand-primary-50)] hover:*:data-icon:text-[var(--gn-color-brand-primary-25)]'
      ].join(' ')
    },
    primary: {
      root: [
        'bg-gray-800 text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset',
        // Inner border gradient
        'before:absolute before:inset-px before:border before:border-white/12 before:mask-b-from-0% before:rounded-[calc(var(--gn-button-border-radius)-1px)]',
        // Disabled styles
        'disabled:bg-disabled disabled:shadow-xs disabled:ring-disabled_subtle',
        // Icon styles
        '*:data-icon:text-button-primary-icon hover:*:data-icon:text-button-primary-icon_hover'
      ].join(' ')
    },
    secondary: {
      root: [
        'bg-primary text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover data-loading:bg-primary_hover',
        // Disabled styles
        'disabled:shadow-xs disabled:ring-disabled_subtle',
        // Icon styles
        '*:data-icon:text-fg-quaternary hover:*:data-icon:text-fg-quaternary_hover'
      ].join(' ')
    },
    tertiary: {
      root: [
        'text-tertiary hover:bg-primary_hover hover:text-tertiary_hover data-loading:bg-primary_hover',
        // Icon styles
        '*:data-icon:text-fg-quaternary hover:*:data-icon:text-fg-quaternary_hover'
      ].join(' ')
    },
    'link-gray': {
      root: [
        'justify-normal rounded p-0! text-tertiary hover:text-tertiary_hover',
        // Inner text underline
        '*:data-text:underline *:data-text:decoration-transparent *:data-text:underline-offset-2 hover:*:data-text:decoration-current',
        // Icon styles
        '*:data-icon:text-fg-quaternary hover:*:data-icon:text-fg-quaternary_hover',
        // Disabled styles
        'disabled:*:data-icon:text-fg-disabled_subtle disabled:text-fg-disabled'
      ].join(' ')
    },
    'link-color': {
      root: [
        'justify-normal rounded p-0! text-brand-secondary hover:text-brand-secondary_hover',
        // Inner text underline
        '*:data-text:underline *:data-text:decoration-transparent *:data-text:underline-offset-2 hover:*:data-text:decoration-current',
        // Icon styles
        '*:data-icon:text-fg-brand-secondary_alt hover:*:data-icon:text-fg-brand-secondary_hover'
      ].join(' ')
    },
    'primary-destructive': {
      root: [
        'bg-error-solid text-white shadow-xs-skeumorphic ring-1 ring-transparent outline-error ring-inset',
        // Inner border gradient
        'before:absolute before:inset-px before:border before:border-white/12 before:mask-b-from-0%',
        // Disabled styles
        'disabled:bg-disabled disabled:shadow-xs disabled:ring-disabled_subtle',
        // Icon styles
        '*:data-icon:text-button-destructive-primary-icon hover:*:data-icon:text-button-destructive-primary-icon_hover'
      ].join(' ')
    },
    'secondary-destructive': {
      root: [
        'bg-primary text-error-primary shadow-xs-skeumorphic ring-1 ring-error_subtle outline-error ring-inset hover:bg-error-primary hover:text-white data-loading:bg-error-primary',
        // Disabled styles
        'disabled:bg-primary disabled:shadow-xs disabled:ring-disabled_subtle',
        // Icon styles
        '*:data-icon:text-fg-error-secondary hover:*:data-icon:text-white'
      ].join(' ')
    },
    'tertiary-destructive': {
      root: [
        'text-error-primary outline-error hover:bg-error-primary hover:text-white data-loading:bg-error-primary',
        // Icon styles
        '*:data-icon:text-fg-error-secondary hover:*:data-icon:text-white'
      ].join(' ')
    },
    'link-destructive': {
      root: [
        'justify-normal rounded p-0! text-error-primary outline-error hover:text-error-primary_hover',
        // Inner text underline
        '*:data-text:underline *:data-text:decoration-transparent *:data-text:underline-offset-2 hover:*:data-text:decoration-current',
        // Icon styles
        '*:data-icon:text-fg-error-secondary hover:*:data-icon:text-fg-error-primary'
      ].join(' ')
    }
  }
});

type ButtonSize = keyof typeof styles.sizes;
type ButtonColor = Extract<keyof typeof styles.colors, string>;

const linkColorOptions: ButtonColor[] = [
  'link-gray',
  'link-color',
  'link-destructive'
];

/**
 * Common props shared between button and anchor variants
 */
type IconRenderable = ReactNode | ComponentType<{ className?: string }>;

export interface CommonProps {
  /** Disables the button and shows a disabled state */
  isDisabled?: boolean;
  /** Shows a loading spinner and disables the button */
  isLoading?: boolean;
  /** Forces the button into its focus-visible state */
  isFocused?: boolean;
  /** The size variant of the button */
  size?: ButtonSize;
  /** The color variant of the button */
  color?: ButtonColor;
  /** Icon component or element to show before the text */
  iconLeading?: IconRenderable;
  /** Icon component or element to show after the text */
  iconTrailing?: IconRenderable;
  /** Removes horizontal padding from the text content */
  noTextPadding?: boolean;
  /** When true, keeps the text visible during loading state */
  showTextWhileLoading?: boolean;
}

/**
 * Props for the button variant (non-link)
 */
export interface ButtonProps
  extends CommonProps,
    DetailedHTMLProps<
      Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color' | 'slot'>,
      HTMLButtonElement
    > {
  /** Slot name for react-aria component */
  slot?: AriaButtonProps['slot'];
}

/**
 * Props for the link variant (anchor tag)
 */
interface LinkProps
  extends CommonProps,
    DetailedHTMLProps<
      Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'color'>,
      HTMLAnchorElement
    > {}

/** Union type of button and link props */
export type Props = ButtonProps | LinkProps;

export const Button = ({
  size = 'sm',
  color = 'primary',
  children,
  className,
  noTextPadding,
  iconLeading: IconLeading,
  iconTrailing: IconTrailing,
  isDisabled: disabled,
  isLoading: loading,
  isFocused,
  showTextWhileLoading,
  ...otherProps
}: Props) => {
  const href = 'href' in otherProps ? otherProps.href : undefined;
  const Component = href ? AriaLink : AriaButton;
  const componentRef = useRef<HTMLButtonElement | HTMLAnchorElement | null>(
    null
  );
  const setComponentRef = (
    node: HTMLButtonElement | HTMLAnchorElement | null
  ) => {
    componentRef.current = node;
  };
  const shouldForceFocus = Boolean(isFocused && !disabled);

  const isIcon = (IconLeading || IconTrailing) && !children;
  const isLinkType = linkColorOptions.includes(color);
  const shouldRemoveTextPadding = isLinkType || noTextPadding;

  const IconLeadingComponent = isReactComponent(IconLeading)
    ? (IconLeading as ComponentType<{ className?: string }>)
    : undefined;
  const IconTrailingComponent = isReactComponent(IconTrailing)
    ? (IconTrailing as ComponentType<{ className?: string }>)
    : undefined;
  const IconLeadingElement = isValidElement(IconLeading) ? IconLeading : null;
  const IconTrailingElement = isValidElement(IconTrailing)
    ? IconTrailing
    : null;

  let props = {};

  if (href) {
    props = {
      ...otherProps,

      href: disabled ? undefined : href,

      // Since anchor elements do not support the `disabled` attribute and state,
      // we need to specify `data-rac` and `data-disabled` in order to be able
      // to use the `disabled:` selector in classes.
      ...(disabled ? { 'data-rac': true, 'data-disabled': true } : {})
    };
  } else {
    props = {
      ...otherProps,

      type: otherProps.type || 'button',
      isPending: loading,
      isDisabled: disabled
    };
  }

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const node = componentRef.current;
    if (!node) {
      return;
    }

    if (shouldForceFocus) {
      requestAnimationFrame(() => node.focus());
    } else if (!shouldForceFocus && document.activeElement === node) {
      node.blur();
    }
  }, [shouldForceFocus]);

  return (
    <Component
      ref={setComponentRef}
      data-force-focus={shouldForceFocus ? true : undefined}
      data-loading={loading ? true : undefined}
      data-icon-only={isIcon ? true : undefined}
      {...props}
      className={cx(
        styles.common.root,
        styles.sizes[size].root,
        styles.colors[color].root,
        'focus-visible:outline-2 focus-visible:outline-offset-2',
        'data-[force-focus=true]:outline-2 data-[force-focus=true]:outline-offset-2',
        isLinkType && styles.sizes[size].linkRoot,
        (loading || (href && (disabled || loading))) && 'pointer-events-none',
        // If in `loading` state, hide everything except the loading icon (and text if `showTextWhileLoading` is true).
        loading &&
          (showTextWhileLoading
            ? '[&>*:not([data-icon=loading]):not([data-text])]:hidden'
            : '[&>*:not([data-icon=loading])]:invisible'),
        className
      )}
    >
      {/* Leading icon */}
      {IconLeadingElement}
      {IconLeadingComponent && (
        <IconLeadingComponent
          data-icon="leading"
          className={styles.common.icon}
        />
      )}

      {loading && (
        <svg
          fill="none"
          data-icon="loading"
          viewBox="0 0 20 20"
          className={cx(
            styles.common.icon,
            !showTextWhileLoading &&
              'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
          )}
        >
          {/* Background circle */}
          <circle
            className="stroke-current opacity-30"
            cx="10"
            cy="10"
            r="8"
            fill="none"
            strokeWidth="2"
          />
          {/* Spinning circle */}
          <circle
            className="origin-center animate-spin stroke-current"
            cx="10"
            cy="10"
            r="8"
            fill="none"
            strokeWidth="2"
            strokeDasharray="12.5 50"
            strokeLinecap="round"
          />
        </svg>
      )}

      {children && (
        <span
          data-text
          className={cx(
            'transition-inherit-all',
            !shouldRemoveTextPadding && 'px-0.5'
          )}
        >
          {children}
        </span>
      )}

      {/* Trailing icon */}
      {IconTrailingElement}
      {IconTrailingComponent && (
        <IconTrailingComponent
          data-icon="trailing"
          className={styles.common.icon}
        />
      )}
    </Component>
  );
};

type ButtonClassOptions = {
  className?: string;
  href?: string;
} & Partial<
  Pick<
    CommonProps,
    'size' | 'color' | 'isLoading' | 'isDisabled' | 'showTextWhileLoading'
  >
>;

export const buttonClass = ({
  className,
  href,
  size = 'sm',
  color = 'primary',
  isLoading,
  isDisabled,
  showTextWhileLoading
}: ButtonClassOptions = {}) => {
  const isLinkType = linkColorOptions.includes(color);

  return cx(
    styles.common.root,
    styles.sizes[size].root,
    styles.colors[color].root,
    'focus-visible:outline-2 focus-visible:outline-offset-2',
    'data-[force-focus=true]:outline-2 data-[force-focus=true]:outline-offset-2',
    isLinkType && styles.sizes[size].linkRoot,
    (isLoading || (href && (isDisabled || isLoading))) && 'pointer-events-none',
    isLoading &&
      (showTextWhileLoading
        ? '[&>*:not([data-icon=loading]):not([data-text])]:hidden'
        : '[&>*:not([data-icon=loading])]:invisible'),
    className
  );
};
